import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO, startOfDay, isSameDay, setHours, setMinutes } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, User, Dog, Cat, Clock, Filter } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { UserAvatar } from '../common/UserAvatar';

interface AppointmentSwimLanesViewProps {
  appointments: any[];
  onSelectAppointment: (appointmentId: string) => void;
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onRescheduleAppointment?: (appointmentId: string, start: Date, end: Date) => void;
  onReassignAppointment?: (appointmentId: string, vetUserId: string) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date; vetUserId?: string }) => void;
  selectedVetIds?: Set<string>;
  teamMembers: TeamMember[]; // REQUIRED: passed from Dashboard
  onToggleAllStaff?: (showAll: boolean) => void; // Toggle show all/hide all staff
}

export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  photo_url?: string;
}

interface VetAvailability {
  id: string;
  vet_user_id: string;
  start_at: string;
  end_at: string;
  availability_type: 'AVAILABLE' | 'SURGERY_BLOCK' | 'UNAVAILABLE' | 'EMERGENCY_ONLY';
  title?: string;
  notes?: string;
  is_recurring?: boolean;
  recurring_template_id?: string;
}

interface TimeSlot {
  time: Date;
  label: string;
}

type SlotMinutes = 15 | 30;
const SLOT_MINUTES_STORAGE_KEY = 'hp_slot_minutes';
const SLOT_MINUTES_EVENT = 'hp_slot_minutes_changed';
// Keep the visual cell size constant; slotMinutes changes the scale (15-min = 2x, 30-min = 1x).
const SLOT_HEIGHT_PX = 32;

type HoursMode = 'business' | '24h';
const HOURS_MODE_STORAGE_KEY = 'hp_swimlane_hours_mode';

type PracticeHoursRow = { day?: string; open?: string; close?: string; closed?: boolean };

const parseHour = (hhmm?: string | null): { hour: number; minute: number } | null => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { hour: h, minute: m };
};

const roundDownToSlot = (minutes: number, slotMinutes: number) => Math.floor(minutes / slotMinutes) * slotMinutes;
const roundUpToSlot = (minutes: number, slotMinutes: number) => Math.ceil(minutes / slotMinutes) * slotMinutes;

const businessHoursForDate = (hours: PracticeHoursRow[] | null | undefined, date: Date, slotMinutes: number) => {
  const fallback = { startMin: 8 * 60, endMinExclusive: 18 * 60 };
  if (!hours || !Array.isArray(hours) || hours.length === 0) return fallback;

  const day = format(date, 'EEEE').toLowerCase();
  const row = hours.find((r) => (r?.day || '').toLowerCase() === day);
  if (!row || row.closed) return fallback;

  const open = parseHour(row.open);
  const close = parseHour(row.close);
  if (!open || !close) return fallback;

  const startMin = Math.max(0, Math.min(23 * 60 + 59, open.hour * 60 + open.minute));
  const endMin = Math.max(0, Math.min(24 * 60, close.hour * 60 + close.minute));

  const snappedStart = roundDownToSlot(startMin, slotMinutes);
  let snappedEndExclusive = roundUpToSlot(endMin, slotMinutes);
  if (snappedEndExclusive <= snappedStart) snappedEndExclusive = snappedStart + slotMinutes;
  if (snappedEndExclusive > 24 * 60) snappedEndExclusive = 24 * 60;

  return { startMin: snappedStart, endMinExclusive: snappedEndExclusive };
};

// Generate time slots between [startMin, endMinExclusive)
const generateTimeSlots = (date: Date, startMin: number, endMinExclusive: number, slotMinutes: number): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let m = startMin; m < endMinExclusive; m += slotMinutes) {
    const slotTime = new Date(date);
    slotTime.setHours(Math.floor(m / 60), m % 60, 0, 0);
    const label = m % 60 === 0 || m === startMin ? format(slotTime, 'h:mm a') : '';
    slots.push({ time: slotTime, label });
  }
  
  return slots;
};

// Get appointment type color - using brand palette
const getAppointmentTypeColor = (appointment: any): string => {
  const title = appointment.title?.toLowerCase() || '';
  const appointmentType = appointment.appointment_type?.toLowerCase() || '';
  
  // Wellness/Preventive care - Mint Green
  if (title.includes('wellness') || title.includes('vaccine') || title.includes('checkup') || 
      title.includes('exam') || appointmentType.includes('wellness') || appointmentType.includes('checkup')) {
    return 'bg-emerald-400 border-emerald-500 text-white';
  }
  
  // Surgery/Procedures - Soft Blue
  if (title.includes('surgery') || title.includes('spay') || title.includes('neuter') || 
      title.includes('dental') || appointmentType.includes('surgery')) {
    return 'bg-blue-500 border-blue-600 text-white';
  }
  
  // Emergency/Urgent - Red (avoid AI purple)
  if (title.includes('emergency') || title.includes('urgent') || appointmentType.includes('emergency')) {
    return 'bg-red-500 border-red-600 text-white';
  }
  
  // Sick visit/Treatment - Warm Orange
  if (title.includes('sick') || title.includes('treatment') || title.includes('recheck') || 
      title.includes('follow') || appointmentType.includes('sick')) {
    return 'bg-orange-400 border-orange-500 text-white';
  }
  
  // Consultation - Soft Blue
  if (title.includes('consultation') || appointmentType.includes('consultation')) {
    return 'bg-blue-400 border-blue-500 text-white';
  }
  
  // Default - Light Gray
  return 'bg-gray-400 border-gray-500 text-white';
};

// Get pet species icon
const getPetIcon = (pet: any) => {
  const species = pet.species?.toLowerCase();
  if (species === 'dog' || species === 'canine') {
    return <Dog className="w-4 h-4" />;
  } else if (species === 'cat' || species === 'feline') {
    return <Cat className="w-4 h-4" />;
  }
  return <span className="text-xs">🐾</span>;
};

// Get status badge color and styling - using solid backgrounds for visibility on colored cards
const getStatusBadge = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-800 dark:text-amber-200', border: 'border-amber-400 dark:border-amber-600 border-dashed', label: 'Pending Approval' };
    case 'scheduled':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-600', label: 'Scheduled' };
    case 'confirmed':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-600', label: 'Confirmed' };
    case 'in_progress':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-600', label: 'In Progress' };
    case 'complete':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-600', label: 'Complete' };
    case 'cancelled':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-600', label: 'Cancelled' };
    case 'no_show':
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-600', label: 'No Show' };
    default:
      return { bg: 'bg-white/90 dark:bg-gray-800/90', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-600', label: 'Scheduled' };
  }
};

const AppointmentSwimLanesView: React.FC<AppointmentSwimLanesViewProps> = ({
  appointments,
  onSelectAppointment,
  currentDate,
  onNavigate,
  onRescheduleAppointment,
  onReassignAppointment,
  onSelectSlot,
  selectedVetIds: externalSelectedVetIds,
  teamMembers, // PASSED FROM DASHBOARD - no fetching needed
  onToggleAllStaff,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false); // No loading state needed since we get teamMembers from props
  const [draggedAppointment, setDraggedAppointment] = useState<any>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{vetId: string, slotTime: Date} | null>(null);
  const [vetAvailability, setVetAvailability] = useState<VetAvailability[]>([]);
  const [dragOverVet, setDragOverVet] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<number>(0); // Offset from top of appointment when drag started
  const gridRefs = React.useRef<{[key: string]: HTMLDivElement | null}>({});
  
  // State for filter options - persisted in localStorage
  const [showAvailableHours, setShowAvailableHours] = useState(() => {
    const saved = localStorage.getItem('showAvailableHours');
    return saved !== 'false'; // Default to true (show)
  });
  
  const [showCancelledAppointments, setShowCancelledAppointments] = useState(() => {
    const saved = localStorage.getItem('showCancelledAppointments');
    return saved === 'true'; // Default to false (hide cancelled)
  });
  
  // Filter dropdown state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Hours mode (business hours vs 24h)
  const [hoursMode, setHoursMode] = useState<HoursMode>(() => {
    const saved = localStorage.getItem(HOURS_MODE_STORAGE_KEY) as HoursMode | null;
    return saved === '24h' ? '24h' : 'business';
  });

  // Slot density (15-min = 2x vertical scale, 30-min = 1x). Persist + sync across views.
  const [slotMinutes, setSlotMinutes] = useState<SlotMinutes>(() => {
    const saved = localStorage.getItem(SLOT_MINUTES_STORAGE_KEY);
    return saved === '30' ? 30 : 15;
  });

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as { slotMinutes?: number } | undefined;
        const next: SlotMinutes = detail?.slotMinutes === 30 ? 30 : 15;
        setSlotMinutes(next);
      } catch {
        // ignore
      }
    };
    window.addEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
    return () => window.removeEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
  }, []);

  // Practice hours for business-hours mode
  const [practiceHours, setPracticeHours] = useState<PracticeHoursRow[] | null>(null);
  useEffect(() => {
    const loadPractice = async () => {
      try {
        if (!user?.practice_id) return;
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) return;
        const resp = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (Array.isArray(data?.hours)) {
          setPracticeHours(data.hours);
          console.info('[Swimlanes][PracticeHours] Loaded', {
            practice_id: user.practice_id,
            date: format(currentDate, 'yyyy-MM-dd'),
            hours: data.hours,
          });
        } else {
          console.info('[Swimlanes][PracticeHours] No hours on practice response', {
            practice_id: user.practice_id,
            date: format(currentDate, 'yyyy-MM-dd'),
            hours: data?.hours,
          });
        }
      } catch {
        // ignore
      }
    };
    loadPractice();
  }, [user?.practice_id, currentDate, isFilterOpen]);
  
  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Use external selectedVetIds if provided, otherwise show all
  const selectedVetIds = externalSelectedVetIds || new Set<string>();
  
  // Calculate if all staff are currently shown
  const allStaffShown = teamMembers.length > 0 && selectedVetIds.size === teamMembers.length;
  
  // State for drag-to-create new appointments
  const [isCreatingSlot, setIsCreatingSlot] = useState(false);
  const [slotCreationStart, setSlotCreationStart] = useState<{vetId: string, slotTime: Date} | null>(null);
  const [slotCreationEnd, setSlotCreationEnd] = useState<{vetId: string, slotTime: Date} | null>(null);

  // Define fetchVetAvailability before useEffects that use it
  const fetchVetAvailability = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!user?.practice_id || teamMembers.length === 0) {
        return;
      }

      // Capture the current date at fetch time to avoid race conditions
      const fetchDate = new Date(currentDate);
      
      // Format date as YYYY-MM-DD for the API using LOCAL date components
      // This ensures we fetch data for the date being displayed, not the UTC date
      const year = fetchDate.getFullYear();
      const month = String(fetchDate.getMonth() + 1).padStart(2, '0');
      const day = String(fetchDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const startTime = performance.now();
      console.log('⏱️ Starting bulk availability fetch for', teamMembers.length, 'members on', dateStr);

      // Use bulk API endpoint (single request for all vets)
      const vetIdsStr = teamMembers.map(m => m.id).join(',');
      
      // Pick a sample vet ID for the path parameter (required by endpoint signature)
      const sampleVetId = teamMembers[0].id;
      
      const url = `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${sampleVetId}?date=${dateStr}&vet_ids=${vetIdsStr}&timezone=${timezone}`;
      console.log('🚀 Bulk API request:', url);
      
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const bulkResponse = await response.json();
          
          // Flatten nested response: {date: {vet_id: [events]}} -> [events]
          const allAvailability: VetAvailability[] = [];
          if (bulkResponse.data && bulkResponse.data[dateStr]) {
            for (const vetKey in bulkResponse.data[dateStr]) {
              const events = bulkResponse.data[dateStr][vetKey];
              allAvailability.push(...events);
            }
          }
          
          const endTime = performance.now();
          console.log(`🏁 Bulk availability fetch complete: ${(endTime - startTime).toFixed(0)}ms for ${allAvailability.length} records`);
          
          // Debug: Log each blocked time record
          allAvailability.forEach((record, idx) => {
            const start = new Date(record.start_at);
            const end = new Date(record.end_at);
          });
          
          setVetAvailability(allAvailability);
        } else {
          console.error('❌ Bulk availability fetch failed:', await response.text());
          setVetAvailability([]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch availability:', error);
        setVetAvailability([]);
      }
    } catch (error) {
      console.error('Failed to fetch vet availability:', error);
    }
  }, [user?.practice_id, teamMembers, currentDate]);

  // Fetch availability after team members are loaded
  useEffect(() => {
    if (teamMembers.length > 0) {
      fetchVetAvailability();
    }
  }, [teamMembers, currentDate, fetchVetAvailability]);

  // Add global mouseup handler to catch releases outside the grid
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isCreatingSlot) {
        handleSlotMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isCreatingSlot, slotCreationStart, slotCreationEnd]);

  // Filter appointments for the current day
  const todayAppointments = useMemo(() => {
    console.log('All appointments:', appointments);
    console.log('Current date:', currentDate);
    
    const filtered = appointments.filter(apt => {
      try {
        const aptDate = parseISO(apt.appointment_date);
        const matchesDate = isSameDay(aptDate, currentDate);
        
        // Filter out cancelled appointments unless explicitly shown
        if (!showCancelledAppointments && apt.status?.toLowerCase() === 'cancelled') {
          return false;
        }
        
        return matchesDate;
      } catch (error) {
        console.error('Error parsing appointment date:', error, apt);
        return false;
      }
    });
    
    return filtered;
  }, [appointments, currentDate, showCancelledAppointments]);

  // Group appointments by assigned vet
  const appointmentsByVet = useMemo(() => {
    console.log('Team members:', teamMembers);
    
    const grouped: { [key: string]: any[] } = {
      unassigned: []
    };

    // Initialize columns for each team member
    teamMembers.forEach(member => {
      grouped[member.id] = [];
    });

    // Group appointments
    todayAppointments.forEach(apt => {
      if (apt.assigned_vet_user_id) {
        if (!grouped[apt.assigned_vet_user_id]) {
          grouped[apt.assigned_vet_user_id] = [];
        }
        grouped[apt.assigned_vet_user_id].push(apt);
      } else {
        grouped.unassigned.push(apt);
      }
    });

    console.log('Final grouped appointments:', grouped);

    // Sort appointments by time within each group
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const timeA = parseISO(a.appointment_date).getTime();
        const timeB = parseISO(b.appointment_date).getTime();
        return timeA - timeB;
      });
    });

    return grouped;
  }, [todayAppointments, teamMembers]);

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    onNavigate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    onNavigate(newDate);
  };

  const goToToday = () => {
    onNavigate(new Date());
  };


  // Filter team members based on selection (if none selected, show none)
  const filteredTeamMembers = useMemo(() => {
    if (selectedVetIds.size === 0) {
      return []; // No doctors selected = show no columns
    }
    return teamMembers.filter(member => selectedVetIds.has(member.id));
  }, [teamMembers, selectedVetIds]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, appointment: any) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appointment.id);
    
    // Calculate where in the appointment box the user grabbed it
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    setDragOffset(offsetY);
    
    console.log('🎯 DRAG START:', {
      appointmentId: appointment.id,
      mouseY: e.clientY,
      boxTop: rect.top,
      grabOffset: offsetY,
      appointmentTime: parseISO(appointment.appointment_date).toLocaleTimeString(),
    });
  };

  const handleDragOver = (e: React.DragEvent, vetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverVet(vetId);
  };

  const handleDragLeave = () => {
    setDragOverVet(null);
  };

  const handleDrop = async (e: React.DragEvent, vetId: string) => {
    e.preventDefault();
    setDragOverVet(null);

    if (!draggedAppointment) return;

    const appointment = draggedAppointment;
    const dragStartTime = parseISO(appointment.appointment_date);
    
    // Get the grid container for this vet
    const gridContainer = gridRefs.current[vetId];
    if (!gridContainer) {
      console.error('No grid container found for vet:', vetId);
      return;
    }
    
    // Get the drop position relative to the entire grid (from top of 8 AM)
    const gridRect = gridContainer.getBoundingClientRect();
    // Adjust for where the user grabbed the appointment (subtract the offset)
    const adjustedOffsetY = (e.clientY - gridRect.top) - dragOffset;
    

    
    // Calculate total minutes from grid start based on pixel position
    const minutesFromGridTop = (adjustedOffsetY / SLOT_HEIGHT_PX) * slotMinutes;
    const rawMinutes = gridStartMin + minutesFromGridTop;
    
    // Snap to nearest slot boundary
    const snappedMinutes = Math.round(rawMinutes / slotMinutes) * slotMinutes;
    
    // Create new start time
    const newStartTime = new Date(currentDate);
    newStartTime.setHours(Math.floor(snappedMinutes / 60));
    newStartTime.setMinutes(snappedMinutes % 60);
    newStartTime.setSeconds(0);
    newStartTime.setMilliseconds(0);
    
    
    // Calculate end time
    const newEndTime = new Date(newStartTime.getTime() + appointment.duration_minutes * 60000);

    // Update appointment time if changed
    const timeChanged = newStartTime.getTime() !== dragStartTime.getTime();
    const vetChanged = vetId !== appointment.assigned_vet_user_id;

    if (timeChanged && onRescheduleAppointment) {
      await onRescheduleAppointment(appointment.id, newStartTime, newEndTime);
    }

    // Update assigned vet if changed
    if (vetChanged && onReassignAppointment) {
      await onReassignAppointment(appointment.id, vetId);
    }

    setDraggedAppointment(null);
  };

  const handleDragEnd = () => {
    setDraggedAppointment(null);
    setDragOverVet(null);
    setDragOffset(0);
  };

  // Handlers for drag-to-create new appointments
  const handleSlotMouseDown = (vetId: string, slotTime: Date, isBlocked: boolean) => {
    if (isBlocked || draggedAppointment) return; // Don't create on blocked slots or when dragging an appointment
    
    setIsCreatingSlot(true);
    setSlotCreationStart({ vetId, slotTime });
    setSlotCreationEnd({ vetId, slotTime });
  };

  const handleSlotMouseEnter = (vetId: string, slotTime: Date) => {
    if (isCreatingSlot && slotCreationStart && slotCreationStart.vetId === vetId) {
      setSlotCreationEnd({ vetId, slotTime });
    }
  };

  const handleSlotMouseUp = () => {
    if (isCreatingSlot && slotCreationStart && slotCreationEnd && onSelectSlot) {
      const startMs = Math.min(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
      const endMs = Math.max(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
      const startTime = new Date(startMs);
      const endTime = new Date(endMs + slotMinutes * 60000); // End of the last slot
      
      onSelectSlot({ 
        start: startTime, 
        end: endTime,
        vetUserId: slotCreationStart.vetId // Pass the vet ID for the selected column
      });
    }
    
    setIsCreatingSlot(false);
    setSlotCreationStart(null);
    setSlotCreationEnd(null);
  };

  // Check if a slot is in the creation range
  const isSlotInCreationRange = (vetId: string, slotTime: Date): boolean => {
    if (!isCreatingSlot || !slotCreationStart || !slotCreationEnd) return false;
    if (vetId !== slotCreationStart.vetId) return false;
    
    const slotTimeMs = slotTime.getTime();
    const startTimeMs = Math.min(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
    const endTimeMs = Math.max(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
    
    return slotTimeMs >= startTimeMs && slotTimeMs <= endTimeMs;
  };

  const { startMin: businessStartMin, endMinExclusive: businessEndMinExclusive } = useMemo(
    () => businessHoursForDate(practiceHours, currentDate, slotMinutes),
    [practiceHours, currentDate, slotMinutes]
  );

  const gridStartMin = hoursMode === '24h' ? 0 : businessStartMin;
  const gridEndMinExclusive = hoursMode === '24h' ? 24 * 60 : businessEndMinExclusive;

  const timeSlots = useMemo(
    () => generateTimeSlots(currentDate, gridStartMin, gridEndMinExclusive, slotMinutes),
    [currentDate, gridStartMin, gridEndMinExclusive, slotMinutes]
  );

  useEffect(() => {
    console.info('[Swimlanes][HoursMode]', {
      mode: hoursMode,
      date: format(currentDate, 'yyyy-MM-dd'),
      businessStartMin,
      businessEndMinExclusive,
      gridStartMin,
      gridEndMinExclusive,
    });
  }, [hoursMode, currentDate, businessStartMin, businessEndMinExclusive, gridStartMin, gridEndMinExclusive]);
  
  if (loading) {
    return (
      <div className="p-6 bg-white flex items-center justify-center h-96">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    );
  }

  type OverlapRenderable =
    | {
        kind: 'blocked';
        id: string;
        startMin: number;
        endMin: number;
        blockedTime: VetAvailability;
      }
    | {
        kind: 'appointment';
        id: string;
        startMin: number;
        endMin: number;
        appointment: any;
      };

  const minutesFromGridStart = (dt: Date): number => {
    return (dt.getHours() * 60 + dt.getMinutes()) - gridStartMin;
  };

  const computeOverlapLayout = (items: OverlapRenderable[]) => {
    // Returns layout info keyed by `${kind}:${id}`.
    const layout = new Map<string, { col: number; cols: number }>();
    if (!items.length) return layout;

    const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

    // Build overlap clusters
    const clusters: OverlapRenderable[][] = [];
    let current: OverlapRenderable[] = [];
    let currentEnd = -Infinity;
    for (const it of sorted) {
      if (!current.length) {
        current = [it];
        currentEnd = it.endMin;
        continue;
      }
      if (it.startMin < currentEnd) {
        current.push(it);
        currentEnd = Math.max(currentEnd, it.endMin);
      } else {
        clusters.push(current);
        current = [it];
        currentEnd = it.endMin;
      }
    }
    if (current.length) clusters.push(current);

    // For each cluster, assign columns and set widths evenly
    for (const cluster of clusters) {
      const byStart = [...cluster].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
      const colEnd: number[] = [];
      const assigned = new Map<string, number>();

      for (const it of byStart) {
        let col = -1;
        for (let i = 0; i < colEnd.length; i++) {
          if (it.startMin >= colEnd[i]) {
            col = i;
            break;
          }
        }
        if (col === -1) {
          col = colEnd.length;
          colEnd.push(it.endMin);
        } else {
          colEnd[col] = it.endMin;
        }
        assigned.set(`${it.kind}:${it.id}`, col);
      }

      const cols = colEnd.length || 1;
      for (const it of cluster) {
        const key = `${it.kind}:${it.id}`;
        layout.set(key, { col: assigned.get(key) ?? 0, cols });
      }
    }

    return layout;
  };
  
  // Calculate the position and size of an appointment from the start of the day
  const getAppointmentStyle = (appointment: any, firstSlotTime: Date) => {
    const aptStartTime = parseISO(appointment.appointment_date);
    
    // Get hours and minutes in local time (same as display)
    const aptHours = aptStartTime.getHours();
    const aptMinutes = aptStartTime.getMinutes();
    
    // Calculate total minutes from 8:00 AM start
    const minutesFromStart = (aptHours * 60 + aptMinutes) - gridStartMin;
    
    const pixelsPerMinute = SLOT_HEIGHT_PX / slotMinutes;
    const topOffset = minutesFromStart * pixelsPerMinute;
    const heightPx = appointment.duration_minutes * pixelsPerMinute;
    
    return {
      top: `${topOffset}px`,
      height: `${heightPx}px`,
    };
  };

  // Get blocked times for a specific vet
  // Note: vetAvailability is already fetched for the specific currentDate
  // The API returns only data for the requested date, so no date filtering needed here
  const getBlockedTimes = (vetId: string) => {
    return vetAvailability.filter(
      avail => avail.vet_user_id === vetId && 
      (avail.availability_type === 'UNAVAILABLE' || avail.availability_type === 'SURGERY_BLOCK')
    );
  };

  // NEW: Get available times for a specific vet
  const getAvailableTimes = (vetId: string) => {
    return vetAvailability.filter(
      avail => avail.vet_user_id === vetId && 
      avail.availability_type === 'AVAILABLE'
    );
  };

  // Check if a slot is blocked
  const isSlotBlocked = (vetId: string, slotTime: Date): VetAvailability | null => {
    const slotEnd = new Date(slotTime.getTime() + slotMinutes * 60000);
    const blocked = vetAvailability.find(avail => {
      if (avail.vet_user_id !== vetId) return false;
      if (avail.availability_type !== 'UNAVAILABLE' && avail.availability_type !== 'SURGERY_BLOCK') return false;
      
      const availStart = parseISO(avail.start_at);
      const availEnd = parseISO(avail.end_at);
      
      // Only mark slot as blocked if it's COMPLETELY covered by the blocked time
      // This prevents marking slots as unclickable when they're only partially blocked
      return availStart <= slotTime && availEnd >= slotEnd;
    });
    
    return blocked || null;
  };

  return (
    <div className="bg-white dark:bg-dark-bg-secondary h-full flex flex-col">
      {/* Date Navigation Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary">
        <div className="flex items-center justify-between">
          {/* Left: Filter Dropdown */}
          <div className="relative z-[200]" ref={filterRef}>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`p-2.5 rounded-lg transition-all duration-200 flex items-center justify-center ${
                isFilterOpen 
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' 
                  : 'bg-white dark:bg-dark-bg-secondary text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-bg-hover hover:text-gray-900 dark:hover:text-gray-200'
              } border border-gray-200 dark:border-dark-border shadow-sm`}
              title="Filters"
            >
              <Filter className="w-5 h-5" />
            </button>
            
            {/* Filter Dropdown Menu - Apple-style with dynamic text */}
            {isFilterOpen && (
              <div className="absolute left-0 top-full mt-2 min-w-[220px] bg-white dark:bg-dark-bg-secondary rounded-lg shadow-2xl border border-gray-200 dark:border-dark-border z-[300] py-1 ring-1 ring-black/5 dark:ring-white/10">
                {/* Work Schedule - dynamic Show/Hide text */}
                <button
                  onClick={() => {
                    const newValue = !showAvailableHours;
                    setShowAvailableHours(newValue);
                    localStorage.setItem('showAvailableHours', String(newValue));
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  {showAvailableHours ? 'Hide' : 'Show'} Work Schedule
                </button>
                
                {/* Cancelled Appointments - dynamic Show/Hide text */}
                <button
                  onClick={() => {
                    const newValue = !showCancelledAppointments;
                    setShowCancelledAppointments(newValue);
                    localStorage.setItem('showCancelledAppointments', String(newValue));
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  {showCancelledAppointments ? 'Hide' : 'Show'} Cancelled
                </button>
                
                {/* All Staff - dynamic Show/Hide text */}
                {onToggleAllStaff && (
                  <button
                    onClick={() => {
                      onToggleAllStaff(!allStaffShown);
                      setIsFilterOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                  >
                    {allStaffShown ? 'Hide' : 'Show'} All Staff
                  </button>
                )}

                {/* Hours range (toggle style like other menu items) */}
                <button
                  onClick={() => {
                    const next: HoursMode = hoursMode === '24h' ? 'business' : '24h';
                    setHoursMode(next);
                    localStorage.setItem(HOURS_MODE_STORAGE_KEY, next);
                    // Let other tabs (Work Schedule) react immediately without needing a remount.
                    try {
                      window.dispatchEvent(new CustomEvent('hp_swimlane_hours_mode_changed', { detail: { mode: next } }));
                    } catch {
                      // ignore
                    }
                    setIsFilterOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  {hoursMode === '24h' ? 'Show Business Hours' : 'Show 24 Hours'}
                </button>

                {/* Slot density (15 vs 30) */}
                <button
                  onClick={() => {
                    const next: SlotMinutes = slotMinutes === 15 ? 30 : 15;
                    setSlotMinutes(next);
                    localStorage.setItem(SLOT_MINUTES_STORAGE_KEY, String(next));
                    try {
                      window.dispatchEvent(new CustomEvent(SLOT_MINUTES_EVENT, { detail: { slotMinutes: next } }));
                    } catch {
                      // ignore
                    }
                    setIsFilterOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  {slotMinutes === 15 ? '30 minute slots' : '15 minute slots'}
                </button>
              </div>
            )}
          </div>
          
          {/* Center: Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={goToPreviousDay}
              className="p-2 hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
            </h2>
            <button
              onClick={goToNextDay}
              className="p-2 hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-md transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          
          {/* Right: Today button */}
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors flex items-center gap-2 border border-blue-200 dark:border-blue-800"
          >
            <Calendar className="w-4 h-4" />
            Today
          </button>
        </div>
      </div>

      {/* Time-Slot Grid */}
      <div className="flex-1 overflow-auto">
        <div className="inline-flex min-w-full">
          {/* Time Column */}
          <div className="w-24 flex-shrink-0 border-r border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-primary">
            <div className="h-12 border-b border-gray-200 dark:border-dark-border sticky top-0 bg-gray-50 dark:bg-dark-bg-primary z-40 shadow-sm ring-1 ring-black/5 dark:ring-white/10"></div>
            {timeSlots.map((slot, idx) => (
              <div
                key={idx}
                className={[
                  'h-8 relative',
                  // Draw exactly ONE horizontal line per slot using border-top.
                  // This avoids doubled lines in 15-min mode.
                  idx === 0 || slot.time.getMinutes() === 0
                    ? 'border-t-2 border-gray-300 dark:border-gray-600'
                    : 'border-t border-gray-100 dark:border-dark-border',
                  idx === timeSlots.length - 1 ? 'border-b border-gray-100 dark:border-dark-border' : '',
                ].join(' ')}
              >
                {slot.label && (
                  <div className="absolute top-0 right-0 pr-3 flex items-center h-8">
                    <span className="text-xs text-gray-500 font-medium">{slot.label}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Vet Columns */}
          {filteredTeamMembers.map(member => {
            const memberAppointments = appointmentsByVet[member.id] || [];
            
            return (
              <div key={member.id} className="flex-1 min-w-[250px] max-w-[400px] border-r border-gray-200 dark:border-dark-border relative">
                {/* Header */}
                <div className="h-12 border-b border-gray-200 dark:border-dark-border px-3 flex items-center bg-gray-50 dark:bg-dark-bg-primary sticky top-0 z-50 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      photoUrl={member.photo_url}
                      fullName={member.full_name || member.email}
                      size="sm"
                    />
                    <div>
                      <div className="font-semibold text-sm text-gray-900 dark:text-dark-text-primary">
                        {member.full_name || member.email.split('@')[0]}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {memberAppointments.length} appts
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Time Slots Grid (background) */}
                <div 
                  ref={(el) => { if (el) gridRefs.current[member.id] = el; }}
                  className="relative"
                  onDragOver={(e) => handleDragOver(e, member.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, member.id)}
                >
                  {timeSlots.map((slot, idx) => {
                    const isDropTarget = dragOverVet === member.id;
                    const blockedAvail = isSlotBlocked(member.id, slot.time);
                    const isBlocked = !!blockedAvail;
                    const isInCreationRange = isSlotInCreationRange(member.id, slot.time);
                    const minute = slot.time.getMinutes();
                    const borderClass = [
                      idx === 0 || minute === 0
                        ? 'border-t-2 border-gray-300 dark:border-gray-600'
                        : 'border-t border-gray-100 dark:border-dark-border',
                      idx === timeSlots.length - 1 ? 'border-b border-gray-100 dark:border-dark-border' : '',
                    ].join(' ');
                    
                    return (
                      <div 
                        key={idx} 
                        className={`h-8 ${borderClass} transition-colors relative ${
                          isBlocked 
                            ? 'cursor-not-allowed pointer-events-none' 
                            : isInCreationRange
                            ? 'bg-blue-200 cursor-pointer'
                            : isDropTarget 
                            ? 'bg-blue-50 cursor-pointer' 
                            : 'bg-white dark:bg-dark-bg-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover cursor-pointer'
                        }`}
                        onMouseDown={() => handleSlotMouseDown(member.id, slot.time, isBlocked)}
                        onMouseEnter={() => handleSlotMouseEnter(member.id, slot.time)}
                        onMouseUp={handleSlotMouseUp}
                      >
                      </div>
                    );
                  })}

                  {/* Blocked + Appointments Overlay (split width when overlapping) */}
                  {(() => {
                    const blockedTimes = getBlockedTimes(member.id);
                    const renderables: OverlapRenderable[] = [];

                    for (const bt of blockedTimes) {
                      const s = parseISO(bt.start_at);
                      const e = parseISO(bt.end_at);
                      const startMin = minutesFromGridStart(s);
                      const endMin = minutesFromGridStart(e);
                      renderables.push({ kind: 'blocked', id: bt.id, startMin, endMin, blockedTime: bt });
                    }

                    for (const appt of memberAppointments) {
                      const s = parseISO(appt.appointment_date);
                      const startMin = minutesFromGridStart(s);
                      const endMin = startMin + (appt.duration_minutes || 0);
                      renderables.push({ kind: 'appointment', id: appt.id, startMin, endMin, appointment: appt });
                    }

                    const layout = computeOverlapLayout(renderables);
                    const pixelsPerMinute = SLOT_HEIGHT_PX / slotMinutes;

                    const positionStyle = (key: string, topPx: number, heightPx: number) => {
                      const l = layout.get(key) || { col: 0, cols: 1 };
                      const widthPct = 100 / (l.cols || 1);
                      const leftPct = (l.col || 0) * widthPct;
                      return {
                        top: `${topPx}px`,
                        height: `${heightPx}px`,
                        left: `calc(${leftPct}% + 4px)`,
                        width: `calc(${widthPct}% - 8px)`,
                      } as React.CSSProperties;
                    };

                    return (
                      <div className="absolute inset-0 pointer-events-none">
                        {blockedTimes.map((blockedTime) => {
                          const blockedStartTime = parseISO(blockedTime.start_at);
                          const blockedEndTime = parseISO(blockedTime.end_at);
                          const startMin = minutesFromGridStart(blockedStartTime);
                          const duration = (blockedEndTime.getTime() - blockedStartTime.getTime()) / 60000;
                          const topOffset = startMin * pixelsPerMinute;
                          const heightPx = duration * pixelsPerMinute;

                          // Title: use custom title if provided, otherwise fall back to availability type
                          let displayTitle = 'Blocked - Doctor out';
                          if (blockedTime.title) {
                            displayTitle = blockedTime.title;
                          } else if (blockedTime.availability_type) {
                            switch (blockedTime.availability_type) {
                              case 'UNAVAILABLE':
                                displayTitle = 'Blocked - Doctor out';
                                break;
                              case 'SURGERY_BLOCK':
                                displayTitle = 'Surgery Block';
                                break;
                              case 'EMERGENCY_ONLY':
                                displayTitle = 'Emergency Only';
                                break;
                            }
                          }

                          const key = `blocked:${blockedTime.id}`;

                          return (
                            <div
                              key={blockedTime.id}
                              className="absolute rounded px-2 py-1 bg-gray-200 border-l-4 border-gray-400 overflow-hidden flex items-center justify-center shadow-sm text-gray-700 z-10"
                              style={positionStyle(key, topOffset, heightPx)}
                            >
                              <span className="text-xs font-medium truncate">{displayTitle}</span>
                            </div>
                          );
                        })}

                        {memberAppointments.map((appointment) => {
                          const aptStart = parseISO(appointment.appointment_date);
                          const startMin = minutesFromGridStart(aptStart);
                          const topOffset = startMin * pixelsPerMinute;
                          const heightPx = (appointment.duration_minutes || 0) * pixelsPerMinute;

                          const isDragging = draggedAppointment?.id === appointment.id;
                          const isCancelled = appointment.status?.toLowerCase() === 'cancelled';
                          const key = `appointment:${appointment.id}`;

                          return (
                            <div
                              key={appointment.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, appointment)}
                              onDragEnd={handleDragEnd}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectAppointment(appointment.id);
                              }}
                              className={`absolute rounded px-2 py-1 cursor-move hover:opacity-90 transition-opacity border-l-4 overflow-hidden pointer-events-auto z-20 ${getAppointmentTypeColor(appointment)} ${
                                isDragging ? 'opacity-50' : ''
                              } ${isCancelled ? 'opacity-70' : ''}`}
                              style={positionStyle(key, topOffset, heightPx)}
                            >
                              <div className="flex items-start justify-between gap-1 h-full">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    {(() => {
                                      const statusBadge = getStatusBadge(appointment.status);
                                      return (
                                        <span
                                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                                        >
                                          {statusBadge.label}
                                        </span>
                                      );
                                    })()}
                                    {appointment.pet_owner?.full_name && (
                                      <span className="text-xs font-semibold truncate">{appointment.pet_owner.full_name}</span>
                                    )}
                                  </div>
                                  {appointment.pets && appointment.pets.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {appointment.pets.slice(0, 2).map((pet: any, petIdx: number) => (
                                        <div key={petIdx} className="flex items-center gap-0.5">
                                          {getPetIcon(pet)}
                                          <span className="text-xs truncate">{pet.name}</span>
                                        </div>
                                      ))}
                                      {appointment.pets.length > 2 && <span className="text-xs">+{appointment.pets.length - 2}</span>}
                                    </div>
                                  )}
                                  {appointment.duration_minutes >= 45 && (
                                    <div className="text-xs opacity-90 truncate mt-0.5">{appointment.title}</div>
                                  )}
                                  <div className="text-xs font-medium mt-1">
                                    {format(parseISO(appointment.appointment_date), 'h:mm a')} -{' '}
                                    {format(
                                      new Date(parseISO(appointment.appointment_date).getTime() + appointment.duration_minutes * 60000),
                                      'h:mm a'
                                    )}
                                  </div>
                                </div>
                                <span className="text-xs font-bold whitespace-nowrap">{appointment.duration_minutes}m</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* NEW: Available Hours Overlay (only shown when toggle is ON) */}
                  {showAvailableHours && (
                    <div className="absolute inset-0 z-5" style={{ pointerEvents: 'none' }}>
                      {getAvailableTimes(member.id).map((availableTime) => {
                        const availStartTime = parseISO(availableTime.start_at);
                        const availEndTime = parseISO(availableTime.end_at);
                        
                        // Calculate position
                        const availHours = availStartTime.getHours();
                        const availMinutes = availStartTime.getMinutes();
                        const minutesFromStart = (availHours * 60 + availMinutes) - gridStartMin;
                        const duration = (availEndTime.getTime() - availStartTime.getTime()) / 60000;
                        const pixelsPerMinute = SLOT_HEIGHT_PX / slotMinutes;
                        const topOffset = minutesFromStart * pixelsPerMinute;
                        const heightPx = duration * pixelsPerMinute;
                        
                        // Get title: use custom title if provided, otherwise default
                        const displayTitle = availableTime.title || 'Available';
                        
                        return (
                          <div
                            key={availableTime.id}
                            className="absolute left-1 right-1 rounded px-2 py-1 bg-green-100 border-l-4 border-green-400 overflow-hidden flex items-center justify-center shadow-sm opacity-60"
                            style={{
                              top: `${topOffset}px`,
                              height: `${heightPx}px`,
                              pointerEvents: 'none',
                            }}
                          >
                            <span className="text-xs text-green-700 font-medium">
                              {displayTitle}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppointmentSwimLanesView;

